import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { ArrowLeftCircle, ArrowRightCircle, Loader2 } from 'lucide-react';

import { getPatientInfoFromPatientId } from '@/utils/cases';
import { CaseImages } from '@/api/cases';
import { type Grade } from '@/api/grade';
import { useTelehealthReportDownload } from '@/hooks/grade-flow/use-telehealth-report-download';

import { Button } from '@/components/ui/button';

type LeftSideDataProps = {
  current?: string;
  images: CaseImages[];
  setExpanded: (img: string) => void;
  patientId?: string;
  showActions?: boolean;
  caseId: string;
  grades?: Grade[];
};

export function InsightsActions(props: LeftSideDataProps) {
  const { setExpanded, current, images, patientId, caseId, showActions = true, grades } = props;
  const { t } = useTranslation();

  const currentIndex = images.findIndex((state) => state.image === current);
  const currentImage = images[currentIndex ?? -1];
  const totalImages = images.length ?? 0;

  const handleMoveLeft = () => {
    if (currentIndex !== undefined && currentIndex > 0) {
      const previousImage = images[currentIndex - 1];
      if (previousImage) {
        setExpanded(previousImage.image);
      }
    }
  };

  const handleMoveRight = () => {
    if (currentIndex !== undefined && currentIndex < totalImages) {
      const nextImage = images[currentIndex + 1];
      if (nextImage) {
        setExpanded(nextImage.image);
      }
    }
  };

  const patient = useMemo(() => {
    if (!patientId) return undefined;

    const patientData = getPatientInfoFromPatientId(patientId);
    return patientData;
  }, [patientId]);

  const { downloadReport, downloading, canDownload } = useTelehealthReportDownload({
    grades,
    caseId,
    patientId,
    patientName: patient?.patientName,
  });

  if (!currentImage) return null;

  return (
    <div className="rounded-lg bg-white bg-opacity-90 p-4 inline-flex flex-col shrink-0 w-full lg:w-[48%] xl:w-52 xl:order-1 order-3">
      {patient ? (
        <>
          <h3 className="text-base/7 font-semibold text-gray-900">{t('patient-info')}</h3>
          <p className="text-sm text-gray-500">
            {t('name-label')}: {patient?.patientName}
          </p>
          <p className="text-sm text-gray-500">
            {t('age-label')}: {patient?.age}
          </p>
        </>
      ) : null}
      {showActions ? (
        <>
          <hr className="my-4" />
          <div className="flex flex-col gap-2">
            <Button
              onClick={() => {
                void downloadReport();
              }}
              disabled={!canDownload || downloading}
              title={
                canDownload
                  ? 'Download telehealth report'
                  : 'Download available once the case has grades'
              }
            >
              <span className="flex items-center gap-2">
                {downloading ? <Loader2 className="size-4 animate-spin" /> : null}
                <span>Download Report</span>
              </span>
            </Button>
            <Button disabled title="Send for Referral - Not Available at this moment">
              Send for Referral
            </Button>
          </div>
        </>
      ) : null}

      <hr className="my-4" />
      <div className="flex justify-between items-center mt-2">
        <Button
          onClick={handleMoveLeft}
          disabled={currentIndex === 0}
          variant="ghost-secondary"
          size="icon"
        >
          <ArrowLeftCircle className="size-6" />
        </Button>
        <span className="text-xs">
          {(currentIndex ?? 0) + 1} {totalImages ? `of ${totalImages}` : ''}
        </span>
        <Button
          onClick={handleMoveRight}
          disabled={!totalImages || currentIndex + 1 === totalImages}
          variant="ghost-secondary"
          size="icon"
        >
          <ArrowRightCircle className="size-6" />
        </Button>
      </div>
    </div>
  );
}
